import { BaseLayerSchemas, BaseStyleId } from "@cartographerio/atlas-map";
import { Point } from "geojson";
import lodash from "lodash";
import mapboxgl from "mapbox-gl";
import { RefObject, useEffect, useState } from "react";

export interface Viewport {
  center: Point;
  zoom: number;
  bearing: number;
  pitch: number;
}

export interface UseMapboxMapProps {
  container: RefObject<HTMLDivElement>;
  mapboxAccessToken: string;
  baseStyles: BaseLayerSchemas;
  baseStyleId: BaseStyleId;
  defaultViewport?: Partial<Viewport>;
  scrollWheel?: boolean;
}

export default function useMapboxMap(
  props: UseMapboxMapProps
): mapboxgl.Map | null {
  const {
    container,
    mapboxAccessToken,
    baseStyles,
    baseStyleId,
    defaultViewport,
    scrollWheel,
  } = props;

  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // UPDATE MAPBOX TOKEN

  useEffect(() => {
    mapboxgl.accessToken = mapboxAccessToken;
  }, [mapboxAccessToken]);

  // CREATE MAPBOX SOURCES AND LAYERS

  // INITIALISE MAPBOX MAP

  useEffect(() => {
    if (container.current == null) return;

    setMapLoaded(false);

    const map = new mapboxgl.Map({
      container: container.current,
      scrollZoom: scrollWheel ?? true,
      hash: true,
      attributionControl: false,
      ...lodash.omitBy(
        {
          zoom: defaultViewport?.zoom,
          center: defaultViewport?.center?.coordinates,
          bounds: defaultViewport?.center?.bbox,
          bearing: defaultViewport?.bearing,
          pitch: defaultViewport?.pitch,
        },
        value => value === undefined
      ),
    });

    map.addControl(new mapboxgl.NavigationControl());
    map.addControl(new mapboxgl.GeolocateControl());
    map.addControl(
      new mapboxgl.AttributionControl({ compact: true }),
      "bottom-right"
    );

    setMap(map);
    setMapLoaded(false);

    const onLoad = () => {
      setMapLoaded(true);
    };

    map.on("load", onLoad);

    map.setStyle(baseStyles[baseStyleId].url);

    return () => {
      map.remove();
      setMap(null);
      setMapLoaded(false);
    };
  }, [container, baseStyles, baseStyleId, defaultViewport, scrollWheel]);

  return mapLoaded ? map : null;
}
